import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup
} from '@angular/forms';
import { finalize, map, Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import {
  ContractorRegistration,
  Document
} from 'src/app/models';

import { Contractor } from 'src/app/models/contractor';
import { ContractorDocument } from 'src/app/models/contractor-document';
import { ContractorRegistrationDocument } from 'src/app/models/contractor-registration-document';
import { RegistrationStatus } from 'src/app/models/registration';

import {
  ContractorService,
  DataEntityFactory
} from 'src/app/services';

import { Utilities } from 'src/app/services/utilities';

import { ContractorCustomFieldListComponent } from 'src/app/components/contractors/contractor-custom-field-list/contractor-custom-field-list.component';

@Component({
  selector: 'app-staff-contractor-registration-details',
  standalone: false,
  templateUrl: './contractor-registration-details.component.html',
  styleUrls: ['./contractor-registration-details.component.scss']
})
export class StaffContractorRegistrationDetailsComponent
  implements OnInit, OnChanges {
  @Input() registrationId = '';
  @Input() closeExpansion!: () => void;

  contractorId = '';

  registration: ContractorRegistration;
  contractor: Contractor;

  RegistrationStatus = RegistrationStatus;

  form: UntypedFormGroup;
  contractorForm: UntypedFormGroup;
  rcfForm: UntypedFormGroup;

  documents: ContractorRegistrationDocument[] = [];
  contractorDocuments: ContractorDocument[] = [];

  contractorRequirementFields: any[] = [];
  registrationRequirementFields: any[] = [];

  isLoading = false;
  savingContractorRequirements = false;
  savingRegistrationRequirements = false;
  savingNote = false;

  showAddNoteModal = false;
  manualNote = '';

  modalTop = 0;
  modalLeft = 0;
  modalWidth = 0;
  modalHeight = 0;

  @ViewChild('customFields', { static: false })
  customFields: ContractorCustomFieldListComponent;

  @ViewChild('registrationCustomFields', {
    static: false
  })
  registrationCustomFields:
    ContractorCustomFieldListComponent;

  constructor(
    private contractorSvc: ContractorService,
    private fb: UntypedFormBuilder,
    private toastrSvc: ToastrService
  ) {}

  ngOnInit(): void {
    this.initializeForms();

    if (this.registrationId) {
      this.loadRegistration().subscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['registrationId'] &&
      !changes['registrationId'].firstChange &&
      this.registrationId
    ) {
      this.initializeForms();
      this.manualNote = '';
      this.loadRegistration().subscribe();
    }
  }

  private initializeForms(): void {
    if (!this.form) {
      this.form = this.fb.group({});
    }

    if (!this.contractorForm) {
      this.contractorForm = this.fb.group({});
    }

    if (!this.rcfForm) {
      this.rcfForm = this.fb.group({});
    }
  }

  loadRegistration(): Observable<ContractorRegistration> {
    this.isLoading = true;

    return this.contractorSvc
      .getContractorRegistration(this.registrationId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        map(registration => {
          if (!registration) {
            this.toastrSvc.error(
              'Registration either does not exist or you do not have permission to view.'
            );

            return null;
          }

          this.registration = registration;
          this.registration.notes =
            this.registration.notes || [];

          this.contractor = registration.contractor;
          this.contractorId = registration.contractorID;
          this.documents = registration.documents || [];

          this.prepareRequirementFields(registration);
          this.loadContractorDocuments();

          return registration;
        })
      );
  }

  private prepareRequirementFields(
    registration: ContractorRegistration
  ): void {
    if (!registration.type?.requirements) {
      this.contractorRequirementFields = [];
      this.registrationRequirementFields = [];
      return;
    }

    const contractorCustomFields = Object.assign(
      {},
      ...(registration.contractor?.customFields || []).map(
        field => ({
          [field.templateCode]: field.fieldValue
        })
      )
    );

    const registrationCustomFields = Object.assign(
      {},
      ...(registration.customFields || []).map(field => ({
        [field.templateCode]: field.fieldValue
      }))
    );

    this.contractorRequirementFields =
      registration.type.requirements
        .filter(
          requirement =>
            requirement.isContractorRequirement
        )
        .map(requirement => {
          const dataEntityDef =
            requirement.dataEntityDefinition ||
            DataEntityFactory.createDataEntity(
              'free-form-text',
              {
                templateCode:
                  requirement.templateCode,
                label: requirement.label
              }
            );

          return {
            label: requirement.label,
            templateCode: requirement.templateCode,
            dataEntityDef,
            dataEntityTypeCode:
              dataEntityDef?.dataEntityTypeCode ||
              'free-form-text',
            dataEntityType:
              dataEntityDef?.dataEntityTypeCode ||
              'free-form-text',
            fieldValue:
              contractorCustomFields[
                requirement.templateCode
              ],
            contractorId: registration.contractorID
          };
        });

    this.registrationRequirementFields =
      registration.type.requirements
        .filter(
          requirement =>
            !requirement.isContractorRequirement
        )
        .map(requirement => {
          const dataEntityDef =
            requirement.dataEntityDefinition ||
            DataEntityFactory.createDataEntity(
              'free-form-text',
              {
                templateCode:
                  requirement.templateCode,
                label: requirement.label
              }
            );

          return {
            label: requirement.label,
            templateCode: requirement.templateCode,
            dataEntityDef,
            dataEntityTypeCode:
              dataEntityDef?.dataEntityTypeCode ||
              'free-form-text',
            dataEntityType:
              dataEntityDef?.dataEntityTypeCode ||
              'free-form-text',
            fieldValue:
              registrationCustomFields[
                requirement.templateCode
              ],
            registrationId: registration.id
          };
        });
  }

  loadContractorDocuments(): void {
    if (!this.contractorId) {
      this.contractorDocuments = [];
      return;
    }

    this.contractorSvc
      .getContractorDocuments(this.contractorId)
      .subscribe(documents => {
        this.contractorDocuments = documents || [];
      });
  }

  loadDocuments(): void {
    this.loadContractorDocuments();

    this.contractorSvc
      .getContractorRegistrationDocuments(
        this.registrationId
      )
      .subscribe(documents => {
        this.documents = documents || [];
      });
  }

  addNewDocuments(documents: Document[]): void {
    const registrationDocuments = documents.map(
      document => {
        const registrationDocument =
          new ContractorRegistrationDocument();

        registrationDocument.documentName =
          document.name;

        registrationDocument.documentPath =
          document.path;

        return registrationDocument;
      }
    );

    this.contractorSvc
      .saveContractorRegistrationDocuments(
        this.registrationId,
        registrationDocuments
      )
      .subscribe(savedDocuments => {
        this.documents = savedDocuments || [];
        this.toastrSvc.success('Document(s) Saved');
      });
  }

  addNewContractorDocuments(
    documents: Document[]
  ): void {
    const contractorDocuments = documents.map(
      document => {
        const contractorDocument =
          new ContractorDocument();

        contractorDocument.documentName =
          document.name;

        contractorDocument.documentPath =
          document.path;

        return contractorDocument;
      }
    );

    this.contractorSvc
      .saveContractorDocuments(
        this.contractorId,
        contractorDocuments
      )
      .subscribe(() => {
        this.loadDocuments();
        this.toastrSvc.success('Document(s) Saved');
      });
  }

  deleteDocument(documentId: string): void {
    this.contractorSvc
      .deleteContractorRegistrationDocument(documentId)
      .subscribe(() => {
        this.loadDocuments();
        this.toastrSvc.success('Document Deleted');
      });
  }

  deleteContractorDocument(
    documentId: string
  ): void {
    this.contractorSvc
      .deleteContractorDocument(documentId)
      .subscribe(() => {
        this.loadDocuments();
        this.toastrSvc.success('Document Deleted');
      });
  }

  saveDocument(
    document: ContractorRegistrationDocument
  ): void {
    this.contractorSvc
      .saveContractorRegistrationDocument(document)
      .subscribe(() => {
        this.toastrSvc.success('Document Saved');
      });
  }

  saveContractorDocument(
    document: ContractorDocument
  ): void {
    this.contractorSvc
      .saveContractorDocument(document)
      .subscribe(() => {
        this.toastrSvc.success('Document Saved');
      });
  }

  saveContractorDetails(): void {
    if (!this.contractor || !this.customFields) {
      return;
    }

    this.contractor.customFields =
      this.customFields.persistValues();

    this.contractor.registrations = [];
    this.contractor.documents = [];
    this.contractor.user = null;

    this.savingContractorRequirements = true;

    this.contractorSvc
      .saveContractorDetails(this.contractor)
      .subscribe({
        next: () => {
          this.loadRegistration().subscribe(() => {
            this.savingContractorRequirements =
              false;

            this.toastrSvc.success(
              'Contractor Saved'
            );
          });
        },
        error: () => {
          this.savingContractorRequirements = false;

          this.toastrSvc.error(
            'Your changes were not saved.',
            'Something went wrong!'
          );
        }
      });
  }

  saveRegistrationCustomFields(): void {
    if (
      !this.registration ||
      !this.registrationCustomFields
    ) {
      return;
    }

    this.registration.customFields =
      this.registrationCustomFields.persistValues();

    this.savingRegistrationRequirements = true;

    this.contractorSvc
      .saveContractorRegistrationCustomFields(
        this.registration.customFields,
        this.contractorId
      )
      .subscribe({
        next: () => {
          this.loadRegistration().subscribe(() => {
            this.savingRegistrationRequirements =
              false;

            this.toastrSvc.success(
              'Registration Saved'
            );
          });
        },
        error: () => {
          this.savingRegistrationRequirements = false;

          this.toastrSvc.error(
            'Your changes were not saved.',
            'Something went wrong!'
          );
        }
      });
  }

  openAddNoteModal(): void {
    this.updateAddNoteModalPosition();

    this.manualNote = '';
    this.showAddNoteModal = true;
  }

  private updateAddNoteModalPosition(): void {
    const container = document.querySelector(
      '.contractors-main-container'
    ) as HTMLElement | null;

    if (!container) {
      this.modalTop = 0;
      this.modalLeft = 0;
      this.modalWidth = window.innerWidth;
      this.modalHeight = window.innerHeight;
      return;
    }

    const rect = container.getBoundingClientRect();

    this.modalTop = rect.top;
    this.modalLeft = rect.left;
    this.modalWidth = rect.width;
    this.modalHeight = rect.height;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.showAddNoteModal) {
      this.updateAddNoteModalPosition();
    }
  }

  closeAddNoteModal(): void {
    if (this.savingNote) {
      return;
    }

    this.manualNote = '';
    this.showAddNoteModal = false;
  }

  addNote(): void {
    const noteText = this.manualNote?.trim();

    if (!noteText) {
      this.toastrSvc.warning('Please enter a note.');
      return;
    }

    if (
      !this.registration?.id ||
      !this.contractor?.id
    ) {
      this.toastrSvc.error(
        'Registration information is unavailable.'
      );
      return;
    }

    this.savingNote = true;

    this.contractorSvc
      .saveRegistrationNote(
        {
          notes: noteText,
          createdBy: '',
          modifiedBy: '',
          createdOn: new Date(),
          registrationID: this.registration.id
        },
        this.contractor.id,
        this.contractor.userId
      )
      .pipe(
        finalize(() => {
          this.savingNote = false;
        })
      )
      .subscribe({
        next: () => {
          this.manualNote = '';
          this.showAddNoteModal = false;

          this.loadRegistration().subscribe(() => {
            this.toastrSvc.success('Note saved');
          });
        },
        error: error => {
          console.error(
            'Unable to save registration note:',
            error
          );

          this.toastrSvc.error(
            'Your note was not saved.',
            'Something went wrong!'
          );
        }
      });
  }

  getAzureDocumentPath(
    document: ContractorDocument
  ): string {
    return Utilities.getAzureUrl(document);
  }

  closeDetails(): void {
    this.closeExpansion?.();
  }
}
